import { useEffect, useState } from "react";
import { Download, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { clp, fmtDate, OVERNIGHT, STATUS, waLink } from "@/lib/format";
import { Btn } from "@/components/admin/AdminUI";

function Receipt({ r }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!r.receipt) return undefined;
    let u;
    api.get(`/admin/reservations/${r.id}/receipt`, { responseType: "blob" }).then((res) => { u = URL.createObjectURL(res.data); setUrl(u); }).catch(() => setUrl("error"));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [r.id, r.receipt]);
  if (!r.receipt) return <p className="text-sm text-muted-foreground">Sin comprobante (reserva manual).</p>;
  if (!url) return <p className="text-sm text-muted-foreground">Cargando comprobante…</p>;
  if (url === "error") return <p className="text-sm text-red-600">No se pudo cargar el comprobante.</p>;
  const pdf = r.receipt.content_type === "application/pdf";
  return (
    <div data-testid="receipt-viewer">
      {pdf ? <iframe title="comprobante" src={url} className="w-full h-96 rounded-xl border" /> : <img src={url} alt="Comprobante" className="max-h-96 rounded-xl border mx-auto" />}
      <a href={url} download={r.receipt.filename} className="mt-2 inline-flex items-center gap-1 text-sm text-[#5E6B52] font-semibold" data-testid="download-receipt-link"><Download className="h-4 w-4" />Descargar</a>
    </div>
  );
}

const Info = ({ k, v }) => <div><p className="text-xs text-muted-foreground">{k}</p><p className="font-medium break-words">{v || "—"}</p></div>;

export function ReservationDetail({ id, onClose, onChanged }) {
  const [r, setR] = useState(null);
  const [amount, setAmount] = useState(0);
  const [reason, setReason] = useState("");
  const [pay, setPay] = useState({ amount: 0, method: "transferencia", note: "" });
  const [edit, setEdit] = useState({});
  const load = () => api.get(`/admin/reservations/${id}`).then((x) => {
    setR(x.data); setAmount(x.data.deposit);
    setEdit({ check_in: x.data.check_in, check_out: x.data.check_out, guests: x.data.guests, units: x.data.units, admin_notes: x.data.admin_notes || "", total_override: "" });
  });
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (path, body, msg) => {
    if ((path === "cancel" || path === "reject") && !window.confirm(path === "cancel" ? "¿Cancelar esta reserva? Se liberará la disponibilidad." : "¿Rechazar esta solicitud?")) return;
    try {
      const x = await api.post(`/admin/reservations/${id}/${path}`, body);
      setR(x.data); onChanged(); toast.success(msg);
      if (path === "confirm") toast[x.data.email_sent ? "success" : "warning"](x.data.email_sent ? "Correo de confirmación enviado" : "No se pudo enviar el correo de confirmación");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const saveEdit = async () => {
    const body = { ...edit, total_override: edit.total_override === "" ? undefined : Number(edit.total_override) };
    if (!OVERNIGHT.includes(r.experience)) body.check_out = body.check_in;
    try { const x = await api.put(`/admin/reservations/${id}`, body); setR(x.data); onChanged(); toast.success("Reserva actualizada"); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!r) return null;
  const c = r.customer || {};
  const on = OVERNIGHT.includes(r.experience);
  const active = ["pendiente", "confirmada"].includes(r.status);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="reservation-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex flex-wrap items-center gap-3">
            <span data-testid="detail-code">{r.code}</span>
            <span className={`rounded-full border px-2.5 py-0.5 text-xs font-sans font-semibold ${STATUS[r.status]?.cls}`} data-testid="detail-status">{STATUS[r.status]?.label}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="grid sm:grid-cols-3 gap-4 text-sm">
          <Info k="Experiencia" v={r.experience_name} />
          <Info k="Fechas" v={on ? `${fmtDate(r.check_in)} → ${fmtDate(r.check_out)} (${r.nights} n.)` : fmtDate(r.check_in)} />
          <Info k="Horario" v={on ? `Check-in ${r.hora_inicio} / out ${r.hora_fin}` : `${r.hora_inicio}–${r.hora_fin}`} />
          <Info k="Personas" v={`${r.guests}${r.billed_guests !== r.guests ? ` (cobra ${r.billed_guests})` : ""}${r.units ? ` · ${r.units} espacio(s)` : ""}`} />
          <Info k="Extras" v={(r.extras || []).map((x) => `${x.name} x${x.qty} (${clp(x.subtotal)})`).join(", ")} />
          <Info k="Origen" v={r.source === "web" ? "Sitio web" : "Manual"} />
          <Info k="Total" v={clp(r.total)} /><Info k="Abono requerido" v={clp(r.deposit)} />
          <Info k="Pagado / Saldo" v={`${clp(r.paid)} / ${clp(r.balance)}`} />
        </div>
        <div className="rounded-2xl bg-[#FAF7F2] p-4 grid sm:grid-cols-3 gap-3 text-sm">
          <Info k="Cliente" v={`${c.nombre} ${c.apellido}`} /><Info k="RUT" v={c.rut} /><Info k="Teléfono" v={c.telefono} />
          <Info k="Correo" v={c.email} /><Info k="Dirección" v={[c.direccion, c.comuna, c.ciudad].filter(Boolean).join(", ")} /><Info k="Observaciones" v={c.observaciones} />
          <a href={waLink(c.telefono, `Hola ${c.nombre}, te escribimos de Rachel Lodge por tu reserva ${r.code}.`)} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 text-[#25D366] font-semibold" data-testid="contact-whatsapp-link"><MessageCircle className="h-4 w-4" />Contactar por WhatsApp</a>
        </div>
        <div><p className="font-semibold mb-2">Comprobante</p><Receipt r={r} /></div>

        {r.status === "pendiente" && (
          <div className="rounded-2xl border border-border p-4 space-y-3" data-testid="confirm-section">
            <p className="font-semibold">Revisar pago</p>
            <div className="flex flex-wrap items-end gap-3">
              <div><Label className="text-xs">Monto verificado</Label><Input type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} className="w-40" data-testid="confirm-amount-input" /></div>
              <Btn onClick={() => act("confirm", { register_deposit: true, amount }, "Reserva confirmada")} data-testid="confirm-reservation-btn">Confirmar pago</Btn>
            </div>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo (para rechazo o cancelación)" rows={2} data-testid="reason-input" />
            <Btn variant="danger" onClick={() => act("reject", { reason }, "Reserva rechazada")} data-testid="reject-reservation-btn">Rechazar</Btn>
          </div>
        )}
        {active && (
          <div className="rounded-2xl border border-border p-4 space-y-3">
            <p className="font-semibold">Registrar pago</p>
            <div className="flex flex-wrap items-end gap-3">
              <Input type="number" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: Number(e.target.value) })} className="w-36" data-testid="payment-amount-input" />
              <select value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} className="h-10 rounded-md border px-3 text-sm bg-white" data-testid="payment-method-select">
                <option value="transferencia">Transferencia</option><option value="efectivo">Efectivo</option><option value="otro">Otro</option>
              </select>
              <Input value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} placeholder="Nota" className="w-48" />
              <Btn variant="outline" onClick={() => act("payments", pay, "Pago registrado")} data-testid="add-payment-btn">Registrar</Btn>
            </div>
            {(r.payments || []).map((p) => <p key={p.id} className="text-sm text-muted-foreground">{p.at?.slice(0, 10)} · {clp(p.amount)} · {p.method} {p.note && `· ${p.note}`}</p>)}
          </div>
        )}
        {active && (
          <div className="rounded-2xl border border-border p-4 space-y-3" data-testid="edit-section">
            <p className="font-semibold">Modificar reserva</p>
            <div className="grid sm:grid-cols-3 gap-3">
              <div><Label className="text-xs">{on ? "Entrada" : "Fecha"}</Label><Input type="date" value={edit.check_in} onChange={(e) => setEdit({ ...edit, check_in: e.target.value })} data-testid="edit-checkin-input" /></div>
              {on && <div><Label className="text-xs">Salida</Label><Input type="date" value={edit.check_out} onChange={(e) => setEdit({ ...edit, check_out: e.target.value })} data-testid="edit-checkout-input" /></div>}
              <div><Label className="text-xs">Personas</Label><Input type="number" value={edit.guests} onChange={(e) => setEdit({ ...edit, guests: Number(e.target.value) })} data-testid="edit-guests-input" /></div>
              {r.units > 0 && <div><Label className="text-xs">Espacios</Label><Input type="number" value={edit.units} onChange={(e) => setEdit({ ...edit, units: Number(e.target.value) })} /></div>}
              <div><Label className="text-xs">Total manual (opcional)</Label><Input type="number" value={edit.total_override} onChange={(e) => setEdit({ ...edit, total_override: e.target.value })} data-testid="edit-total-override-input" /></div>
            </div>
            <Textarea value={edit.admin_notes} onChange={(e) => setEdit({ ...edit, admin_notes: e.target.value })} placeholder="Notas internas" rows={2} />
            <div className="flex flex-wrap gap-2">
              <Btn variant="outline" onClick={saveEdit} data-testid="save-edit-btn">Guardar cambios</Btn>
              <Btn variant="danger" onClick={() => act("cancel", { reason }, "Reserva cancelada")} data-testid="cancel-reservation-btn">Cancelar reserva</Btn>
            </div>
          </div>
        )}
        <div className="text-xs text-muted-foreground space-y-1">
          {(r.history || []).map((h, i) => <p key={i}>{h.at?.slice(0, 16).replace("T", " ")} · {h.action} {h.note && `— ${h.note}`}</p>)}
        </div>
      </DialogContent>
    </Dialog>
  );
}
